import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import moment from 'moment';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';

import { Dietitian } from '../../../../../../../../data/dietitian/domain/dietitian';
import { EncouragementTemplate } from '../../../../../../../../data/dietitian/domain/encouragement_template/encouragement-template';
import { PatientGroup } from '../../../../../../../../data/dietitian/domain/patient_group/patient_group';
import { Patient } from '../../../../../../../../data/patient/domain/patient';
import { AddEncouragementModalFacade } from './add-encouragement-modal.facade';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-add-encouragement-modal',
  templateUrl: './add-encouragement-modal.component.html',
  styleUrls: ['./add-encouragement-modal.component.scss'],
  providers: [AddEncouragementModalFacade],
})
export class AddEncouragementModalComponent implements OnInit {
  @Input() public dietitian: Dietitian;
  @Input() public target: Patient | Patient[] | PatientGroup;
  @Output() validate = new EventEmitter();
  public form = new FormGroup({
    // title: new FormControl('', [Validators.required, Validators.minLength(1)]),
    message: new FormControl('', [
      Validators.required,
      Validators.minLength(1),
    ]),
    programed: new FormControl(false),
    date: new FormControl(''),
    time: new FormControl(''),
  });
  public isLoading = false;
  public currentEncouragementTemplate: EncouragementTemplate;
  public emptyEncouragementTemplate: EncouragementTemplate;
  public encouragementsTemplates: EncouragementTemplate[];

  constructor(
    private bsModalRef: BsModalRef,
    private toastr: ToastrService,
    private facade: AddEncouragementModalFacade,
  ) {
    this.facade
      .loadAllEncouragementTemplate()
      .then((templates) => (this.encouragementsTemplates = templates));
  }

  ngOnInit(): void {
    this.emptyEncouragementTemplate =
      this.facade.getEmptyEncouragementTemplate();
    this.currentEncouragementTemplate = this.emptyEncouragementTemplate;
  }

  public hideModal(): void {
    this.bsModalRef.hide();
  }

  targetName(): string {
    if (Array.isArray(this.target)) {
      if (this.target.length > 1) {
        return this.target.length + ' patients';
      } else {
        return this.target[0].fullName;
      }
    } else {
      if (this.target instanceof Patient) {
        return this.target.fullName;
      } else {
        return "tous les patients d'un groupe";
      }
    }
  }

  public get destination() {
    if (Array.isArray(this.target)) {
      if (this.target.length > 1) {
        return `Une sélection de ${this.target.length} patients`;
      } else {
        return `${this.target[0]?.fullName}`;
      }
    } else {
      if (this.target instanceof Patient) {
        return `${this.target?.fullName}`;
      } else {
        if (this.target) {
          return `${this.target?.name}`;
        } else {
          return 'tous les patients';
        }
      }
    }
  }

  public get messagePlaceholder() {
    if (Array.isArray(this.target)) {
      return `Ajouter un message d’encouragement pour vos patients sélectionnés. Ils recevront une notification avec votre message.`;
    } else {
      if (this.target instanceof Patient) {
        return `Ajouter un message d’encouragement pour votre patient. Il ou elle recevra une notification avec votre message.`;
      } else {
        return `Ajouter un message d’encouragement pour les patients issus du group "${this.target?.name}". Ils recevront une notification avec votre message.`;
      }
    }
  }

  get isFormValid(): boolean {
    if (!this.form.get('programed')?.value) {
      return this.form.valid;
    } else {
      return (
        this.form.valid &&
        this.form.get('date')?.value &&
        this.form.get('time')?.value
      );
    }
  }

  get isProgramedEncouragement(): boolean {
    return this.form.get('programed')?.value;
  }

  public async sendEncouragement() {
    if (!this.isLoading) {
      const programed = this.form.get('programed');
      const formDate = this.form.get('date');
      const formTime = this.form.get('time');
      const title = this.form.get('title');
      const message = this.form.get('message');

      if (!message) {
        return;
      }

      let programedDate: moment.Moment | undefined;
      if (programed && programed.value) {
        if (!formDate?.value || !formTime?.value) {
          return;
        }

        // merging date and time
        programedDate = moment(formDate.value).startOf('day');
        const time = moment(formTime.value, 'HH:mm');
        programedDate.set({
          hours: time.hours(),
          minutes: time.minutes(),
        });
      }

      this.isLoading = true;

      if (
        this.currentEncouragementTemplate === this.emptyEncouragementTemplate
      ) {
        this.currentEncouragementTemplate.title = title?.value;
        this.currentEncouragementTemplate.content = message.value;
        this.facade
          .saveEncouragementTemplate(this.currentEncouragementTemplate)
          .then()
          .catch((e) => console.error(e));
      }

      // TODO: handle exceptions or re-throw them
      this.facade
        .saveEncouragement(
          this.dietitian.dietitianId,
          message.value,
          this.destination,
          this.target,
          programedDate?.toDate(),
        )
        .catch((err) => {
          if (err instanceof Error) {
            this.toastr.error(err.message);
          } else {
            console.error('Error while sending encouragement', err);
          }
        })
        .then(() => {
          this.hideModal();
          this.validate?.emit();
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }

  get titleInvalid(): boolean | undefined {
    return (
      !this.form.get('title')?.valid &&
      (this.form.get('title')?.touched || this.form.get('title')?.dirty)
    );
  }

  get messageInvalid(): boolean | undefined {
    return (
      !this.form.get('message')?.valid &&
      (this.form.get('message')?.touched || this.form.get('message')?.dirty)
    );
  }

  get dateInvalid(): boolean | undefined {
    return (
      !this.form.get('date')?.valid &&
      (this.form.get('date')?.touched || this.form.get('date')?.dirty)
    );
  }

  onChangeEncouragementTemplate(template: EncouragementTemplate) {
    this.currentEncouragementTemplate = template;
    this.form.get('title')?.setValue(template.title);
    this.form.get('message')?.setValue(template.content);
  }
}
